<template>
  <b-form v-if="ready" @submit.stop.prevent="onSubmit">
    <b-container>
      <b-row>
        <b-col class="pt-3 pb-2">
          <b-button variant="outline-primary" class="mr-2" @click="exit"
            ><b-icon-arrow-left
          /></b-button>
          <strong>{{ pageHeading }}</strong>
        </b-col>
      </b-row>
      <b-row class="mb-2">
        <b-col>
          <b-form-group
            class="mb-0"
            :state="validateField('message')"
            :invalid-feedback="invalidFeedback('message')"
          >
            <si-editor :value="message" @input="onInputEditor" ref="editor" />
          </b-form-group>
        </b-col>
      </b-row>
      <b-row class="mb-2">
        <b-col>
          <div class="bg-white border rounded px-2">
            <b-row>
              <b-col
                class="
                  d-flex
                  justify-content-between
                  align-items-center
                  form-option-container
                "
              >
                <span>Add to your post</span>
                <b-button
                  v-if="!hasImage"
                  size="sm"
                  variant="outline-primary"
                  @click="onAddImage"
                  ><b-icon-image /> Add Image</b-button
                >
                <b-button
                  v-if="hasImage"
                  size="sm"
                  variant="outline-danger"
                  @click="onRemoveImage"
                  ><b-icon-image /> Remove Image</b-button
                >
              </b-col>
            </b-row>
            <b-row v-if="hasImage && canUploadImage" class="mb-2">
              <b-col>
                <b-form-group
                  class="mb-0"
                  :invalid-feedback="invalidFeedback('imageFile')"
                  :state="validateField('imageFile')"
                >
                  <b-form-file
                    v-model="$v.imageFile.$model"
                    accept=".jpg, .jpeg, .png, .gif"
                    placeholder="Choose a file or drop it here..."
                    drop-placeholder="Drop file here..."
                    @input="onInputImageFile"
                  ></b-form-file>
                </b-form-group>
              </b-col>
            </b-row>
            <b-row v-if="hasImage && !canUploadImage" class="mb-2">
              <b-col>
                <b-form-group
                  class="mb-0"
                  :invalid-feedback="invalidFeedback('imageUrl')"
                  :state="validateField('imageUrl')"
                >
                  <b-form-input
                    v-model="$v.imageUrl.$model"
                    placeholder="Paste URL of image..."
                    @input="onInputImageUrl"
                  ></b-form-input>
                </b-form-group>
              </b-col>
            </b-row>
            <b-row v-if="hasImage" class="mb-2">
              <b-col cols="12" md="6">
                <b-icon
                  icon="three-dots"
                  animation="cylon"
                  v-show="isImagePreviewLoading"
                />
                <b-img
                  v-show="
                    imageData || (imageUrl.length && !hasImagePreviewError)
                  "
                  :src="imageData || imageUrl"
                  fluid
                  rounded
                  alt=""
                ></b-img>
              </b-col>
            </b-row>
          </div>
        </b-col>
      </b-row>
      <b-row v-if="canAssignCategory" class="mb-2">
        <b-col>
          <div class="bg-white border rounded px-2 py-2">
            <b-row>
              <b-col class="d-flex align-items-center">Category</b-col>
              <b-col
                ><b-form-select
                  v-model="categoryId"
                  :options="postCategoryOptions"
                  size="sm"
              /></b-col>
            </b-row>
          </div>
        </b-col>
      </b-row>
      <b-row v-if="canSendAlert" class="mb-2">
        <b-col>
          <div
            class="
              bg-white
              border
              rounded
              px-2
              d-flex
              justify-content-between
              align-items-center
              form-option-container
            "
          >
            <span>Send text alert to <strong>all</strong> members</span>
            <b-form-checkbox
              v-model="hasAlert"
              name="check-button"
              switch
              size="lg"
              :disabled="!isSendAlertEnabled"
              @input="onInputSendAlert"
            />
          </div>
        </b-col>
      </b-row>
      <b-row>
        <b-col cols="4" class="pr-1">
          <b-button block variant="outline-secondary" @click="exit"
            >Cancel</b-button
          >
        </b-col>
        <b-col cols="8" class="pl-1">
          <b-button
            block
            variant="primary"
            type="submit"
            :disabled="!canSubmit"
            >{{ submitButtonLabel }}</b-button
          >
        </b-col>
      </b-row>
    </b-container>
  </b-form>
</template>

<script>
import { validationMixin } from "vuelidate";
import { http } from "@/http";
import {
  imagePreview,
  imageSize,
  imageType,
  imageUrl,
  postContent,
} from "@/validators";
import Editor from "@/components/message-board/Editor";
import errorHandler from "@/services/errorHandler";
import errorToast from "@/services/errorToast";
import successToast from "@/services/successToast";
import postCategory from "@/services/postCategory";

let _from = null;

export default {
  name: "MessageEdit",
  components: {
    "si-editor": Editor,
  },
  mixins: [validationMixin],
  validations: {
    message: {
      postContent,
    },
    imageFile: {
      imageSize,
      imageType,
    },
    imageUrl: {
      imagePreview,
      imageUrl,
    },
  },
  data: () => ({
    byMod: false,
    categoryId: "0",
    message: "",
    hasImage: false,
    hasImagePreviewError: false,
    hasAlert: false,
    image: {
      name: "",
      size: 0,
      type: "",
    },
    imageData: "",
    imageFile: null,
    imageUrl: "",
    isImagePreviewLoading: false,
    isMod: false,
    ready: false,
  }),
  computed: {
    canAssignCategory() {
      return (
        (this.isMod && this.byMod && this.isEditing) ||
        (this.isMod && !this.isEditing)
      );
    },
    canSendAlert() {
      return (
        (this.isMod && this.byMod && this.isEditing) ||
        (this.isMod && !this.isEditing)
      );
    },
    canSubmit() {
      return !this.$v.$invalid && !this.isImagePreviewLoading;
    },
    canUploadImage() {
      return (
        (this.isMod && this.byMod && this.isEditing) ||
        (this.isMod && !this.isEditing)
      );
    },
    isSendAlertEnabled() {
      return this.hasMessage || this.hasText;
    },
    hasMessage() {
      return this.message.length > 0 && this.message !== "<p><br></p>";
    },
    hasText() {
      return (
        !!this.$refs.editor && this.$refs.editor.getText().trim().length > 0
      );
    },
    id() {
      return this.$route.params.id || 0;
    },
    isEditing() {
      return !!this.id;
    },
    page() {
      return this.$store.getters.page;
    },
    pageHeading() {
      return `${this.isEditing ? "Update" : "New"} Message`;
    },
    postCategoryOptions() {
      return postCategory.categoryOptions();
    },
    submitButtonLabel() {
      return this.isEditing ? "Update" : "Post";
    },
  },
  methods: {
    confirmRemoveImage() {
      return this.$bvModal
        .msgBoxConfirm("Are you sure you want to remove the image?", {
          cancelTitle: "No",
          centered: true,
          okTitle: "Yes",
          okVariant: "danger",
          title: "Remove Image",
        })
        .then((value) => value)
        .catch((err) => {
          console.error(err);
          return null;
        });
    },
    confirmSendAlert() {
      return this.$bvModal
        .msgBoxConfirm(
          "Are you sure you want to send a text alert to ALL members?",
          {
            cancelTitle: "No",
            centered: true,
            noCloseOnBackdrop: true,
            noCloseOnEsc: true,
            okTitle: "Yes",
            okVariant: "primary",
            title: "Send Text Alert",
          }
        )
        .then((value) => value)
        .catch((err) => {
          console.error(err);
          return null;
        });
    },
    exit() {
      if (!_from || !_from.name) {
        this.$router.push({ name: "Home" });
        return;
      }
      this.$router.back();
    },
    fetchPost() {
      this.$store.dispatch("showBusy");
      this.$store
        .dispatch("fetchPost", { id: this.id })
        .then((post) => {
          this.byMod = post.byMod;
          this.hasImage = !!post.imageUrl.length;
          this.message = post.message;
          this.imageUrl = post.imageUrl;
          this.categoryId = post.categoryId.toString();
          this.ready = true;
          this.$store.dispatch("hideBusy");
        })
        .catch((err) => {
          errorHandler(this, err);
          this.$store.dispatch("hideBusy");
          this.exit();
        });
    },
    invalidFeedback(name) {
      if (name === "message") {
        if (!this.$v.message.postContent)
          return "Please enter a message and/or add an image.";
      }
      if (name === "imageFile") {
        if (!this.$v.imageFile.imageSize) return "Maximum file size is 2 MB.";
        if (!this.$v.imageFile.imageType)
          return "Supported image formats include GIF, JPG, and PNG.";
      }
      if (name === "imageUrl") {
        if (!this.$v.imageUrl.imageUrl || !this.$v.imageUrl.imagePreview) {
          return "Please enter a valid image URL.";
        }
      }
      return "";
    },
    onAddImage() {
      this.hasImage = true;
    },
    onInputEditor($event) {
      this.message = $event;
      this.$v.message.$touch();
    },
    onInputImageFile() {
      if (!this.imageFile) return;
      const reader = new FileReader();
      reader.onload = () => {
        this.imageData = reader.result;
        this.image = {
          name: this.imageFile.name,
          size: this.imageFile.size,
          type: this.imageFile.type,
        };
        this.$v.message.$touch();
      };
      reader.readAsDataURL(this.imageFile);
    },
    onInputImageUrl() {
      if (!this.imageUrl.length) {
        this.hasImagePreviewError = false;
        this.isImagePreviewLoading = false;
        this.$v.message.$touch();
        return;
      }
      this.isImagePreviewLoading = true;
      const image = new Image();
      image.onload = () => {
        this.hasImagePreviewError = false;
        this.isImagePreviewLoading = false;
        this.$v.message.$touch();
      };
      image.onerror = () => {
        this.hasImagePreviewError = true;
        this.isImagePreviewLoading = false;
        this.$v.message.$touch();
      };
      image.src = this.imageUrl;
    },
    onInputSendAlert($event) {
      // when mod chooses to send a text alert, automatically
      // change post category to Shark Alerts
      if ($event) {
        const sharkAlertsCategoryId = "2";
        this.categoryId = sharkAlertsCategoryId;
      }
    },
    async onRemoveImage() {
      if (this.imageData.length || this.imageUrl.length) {
        let result = await this.confirmRemoveImage();
        if (!result) return;
      }
      this.image = {
        name: "",
        size: 0,
        type: "",
      };
      this.imageData = "";
      this.imageUrl = "";
      this.imageFile = null;
      this.hasImage = false;
      this.hasImagePreviewError = false;
      this.$v.message.$touch();
    },
    async onSubmit() {
      this.$v.$touch();
      if (this.$v.$invalid) return;

      if (this.hasAlert && this.isSendAlertEnabled) {
        let result = await this.confirmSendAlert();
        if (!result) {
          this.hasAlert = false;
        }
      }

      let post = {
        message: {
          html: this.message,
          text: this.$refs.editor.getText(),
        },
        image: {
          url: this.imageUrl,
        },
        categoryId: this.categoryId,
      };

      if (this.imageData) {
        Object.assign(post.image, {
          data: this.imageData,
          name: this.image.name,
          size: this.image.size,
          type: this.image.type,
        });
      }

      this.$store.dispatch("showBusy");

      if (this.isEditing) {
        post.id = this.id;
        this.$store
          .dispatch("updatePost", post)
          .then(() => {
            this.$store.dispatch("hideBusy");
            if (this.hasAlert && this.isSendAlertEnabled) {
              this.send(post.message.text);
            }
            this.exit();
          })
          .catch((err) => {
            errorHandler(this, err);
            this.$store.dispatch("hideBusy");
          });
      } else {
        this.$store
          .dispatch("insertPost", post)
          .then(() => {
            this.$store.dispatch("hideBusy");
            if (this.hasAlert && this.isSendAlertEnabled) {
              this.send(post.message.text);
            }
            this.$store.dispatch("setPage", 1);
            this.$router.push({ name: "Home" });
          })
          .catch((err) => {
            errorHandler(this, err);
            this.$store.dispatch("hideBusy");
          });
      }
    },
    send(text) {
      let message = `${text}\n**Not Investment Advice`;
      return http
        .post("/active-trader/alerts/send", { text: message })
        .then(() => {
          successToast(this, "Text alert sent!");
        })
        .catch((err) => {
          errorToast(this, "Text alert failed.");
          console.error(err.message);
          throw err;
        });
    },
    validateField(name) {
      const { $dirty, $error } = this.$v[name];
      return $dirty ? !$error : null;
    },
  },
  mounted() {
    this.$store.dispatch("fetchMember").then((member) => {
      this.isMod = member.isMod;
    });
    if (this.isEditing) {
      this.fetchPost();
    } else {
      this.ready = true;
    }
  },
  beforeRouteEnter(to, from, next) {
    _from = from;
    next();
  },
  beforeRouteUpdate(to, from, next) {
    next(false);
  },
  beforeDestroy() {
    this.$store.dispatch("hideBusy");
  },
};
</script>
